/**
 * src/hooks/useCrates.ts — One-shot crate fetch for the scheduling modal.
 *
 * Resolves customerEmail → UUID, fetches crates for that customer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { queryTable } from '../api/harperClient';
import type { Crate } from '../types';

interface CustomerRecord {
  uuid: string;
  email: string;
}

type CratesState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'success'; crates: Crate[] };

interface UseCratesResult {
  crates: Crate[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches crates for the authenticated customer.
 * @param customerEmail — the authenticated user's email (used to resolve UUID)
 * @param token — current auth token; hook is inert when null
 */
export function useCrates(
  customerEmail: string | null,
  token: string | null,
): UseCratesResult {
  const [state, setState] = useState<CratesState>({ phase: 'loading' });
  const customerUuidRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);
  // Increment to trigger re-fetch without changing other deps
  const [fetchCount, setFetchCount] = useState(0);

  const fetch = useCallback(async () => {
    if (!customerEmail || !token) return;

    setState({ phase: 'loading' });

    // Resolve email → UUID (cached in ref after first successful resolve)
    if (!customerUuidRef.current) {
      try {
        const customers = await queryTable<CustomerRecord>('Customer', {
          email: customerEmail,
        });
        if (customers.length > 0 && customers[0]) {
          customerUuidRef.current = customers[0].uuid;
        } else {
          if (!unmountedRef.current) {
            setState({ phase: 'error', message: 'Customer record not found.' });
          }
          return;
        }
      } catch (err) {
        if (!unmountedRef.current) {
          const msg =
            err instanceof Error ? err.message : 'Failed to resolve customer.';
          setState({ phase: 'error', message: msg });
        }
        return;
      }
    }

    try {
      const cratesData = await queryTable<Crate>('Crate', { customer_id: customerUuidRef.current });
      if (unmountedRef.current) return;
      const crates = Array.isArray(cratesData) ? cratesData : [];
      setState({ phase: 'success', crates });
    } catch (err) {
      if (!unmountedRef.current) {
        const msg =
          err instanceof Error ? err.message : 'Failed to fetch crates.';
        setState({ phase: 'error', message: msg });
      }
    }
  }, [customerEmail, token]);

  useEffect(() => {
    unmountedRef.current = false;
    void fetch();
    return () => {
      unmountedRef.current = true;
    };
  }, [fetch, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((n) => n + 1);
  }, []);

  return {
    crates: state.phase === 'success' ? state.crates : [],
    isLoading: state.phase === 'loading',
    error: state.phase === 'error' ? state.message : null,
    refetch,
  };
}
